// Domain formatting + label utilities.

export function formatCurrency(value, currency = "USD") {
    if (value == null || value === "") return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return "—";
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num);
}

export function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "—";
        // DD/MM/YYYY (Phase 2 — global date format)
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        return `${dd}/${mm}/${d.getFullYear()}`;
    } catch {
        return "—";
    }
}

export function formatDateTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "—";
        // DD/MM/YYYY HH:mm (24-hour). Phase 2.
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        return `${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`;
    } catch {
        return "—";
    }
}

export function daysUntil(isoDate) {
    if (!isoDate) return null;
    const target = new Date(isoDate);
    if (Number.isNaN(target.getTime())) return null;
    const today = new Date();
    target.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return Math.round((target - today) / (1000 * 60 * 60 * 24));
}


// ── Iter60.p9 — Timezone-safe HH:MM helpers ───────────────────────────
// The Attendance table renders clock times via
// `new Date(iso).toLocaleTimeString(...)`, which uses the BROWSER LOCAL
// timezone. Any UI that reads/writes those same fields (Edit Attendance
// modal, quick-adjust dialogs, exports, …) MUST use these two helpers
// so we stay symmetric with the table renderer.
//
// The classic bug was:
//   isoToHM(iso) { d.getUTCHours() + ":" + d.getUTCMinutes(); }
// which showed 05:00 in the modal for a row the table (correctly)
// showed as 08:00 in Africa/Mogadishu (UTC+3). A user "fixing" the
// modal value would then shift the stored UTC by 3 hours.

/**
 * Convert a UTC ISO datetime string to a local `HH:MM` string.
 * Uses the browser's local timezone so the value matches the
 * Attendance table (which is the source of truth for display).
 * Returns "" if input is falsy or unparseable.
 */
export function formatLocalHHMM(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * Inverse of `formatLocalHHMM` for submitting a form time back to the
 * backend. Takes a work date (`YYYY-MM-DD`) and a local time
 * (`HH:MM`), constructs the local Date, and returns a UTC ISO string.
 * The backend `_combine_time` helper accepts ISO strings and will
 * normalise them to UTC.
 *
 * Passing null / "" for `hm` returns null (caller should send the
 * server `null` to clear the field).
 */
export function localHHMMToUtcIso(workDate, hm) {
    if (!workDate || !hm) return null;
    const parts = String(workDate).split("-");
    const hmParts = String(hm).split(":");
    if (parts.length !== 3 || hmParts.length !== 2) return null;
    const y = Number(parts[0]);
    const mo = Number(parts[1]);
    const d = Number(parts[2]);
    const h = Number(hmParts[0]);
    const mi = Number(hmParts[1]);
    if ([y, mo, d, h, mi].some((n) => Number.isNaN(n))) return null;
    // Local Date. `.toISOString()` returns UTC.
    return new Date(y, mo - 1, d, h, mi, 0, 0).toISOString();
}

export function initials(name) {
    if (!name) return "?";
    return name
        .split(" ")
        .filter(Boolean)
        .map((s) => s[0]?.toUpperCase())
        .slice(0, 2)
        .join("");
}

export const EMPLOYMENT_TYPE_LABEL = {
    casual: "Casual",
    non_casual: "Non-Casual",
};

export const WORKER_SUBTYPE_LABEL = {
    manual: "Manual",
    non_manual: "Non-Manual",
};

export const WORKER_CATEGORY_LABEL = {
    casual: "Casual",
    non_casual_manual: "Non-Casual · Manual",
    non_casual_non_manual: "Non-Casual · Non-Manual",
};

export const EMPLOYEE_STATUS_LABEL = {
    active: "Active",
    suspended: "Suspended",
    resigned: "Resigned",
    terminated: "Terminated",
    replaced: "Replaced",
};

export const CONTRACT_TYPE_LABEL = {
    casual_engagement: "Casual Engagement",
    smart_contract_manual: "Smart Contract · Manual",
    smart_contract_non_manual: "Smart Contract · Non-Manual",
    renewal: "Renewal",
    termination: "Termination",
    acknowledgment: "Acknowledgment",
};

export const CONTRACT_STATUS_LABEL = {
    draft: "Draft",
    active: "Active",
    expired: "Expired",
    terminated: "Terminated",
};

export const SALARY_TYPE_LABEL = {
    hourly: "Hourly",
    daily: "Daily",
    weekly: "Weekly",
    bi_weekly: "Bi-Weekly",
    monthly: "Monthly",
};

export const PAYMENT_FREQ_LABEL = SALARY_TYPE_LABEL;

export const PAYMENT_METHOD_LABEL = {
    bank_transfer: "Bank Transfer",
    mobile_money: "Mobile Money",
    cash: "Cash",
};

export const WAGE_STATUS_LABEL = {
    draft: "Draft",
    pending_approval: "Pending Approval",
    approved: "Approved",
    inactive: "Inactive",
};

// Status -> badge color class (Tailwind utility chain).
export const STATUS_BADGE = {
    active: "bg-emerald-50 text-emerald-700",
    approved: "bg-emerald-50 text-emerald-700",
    inactive: "bg-gray-100 text-gray-500",
    draft: "bg-gray-100 text-gray-600",
    pending_approval: "bg-amber-50 text-amber-700",
    pending: "bg-amber-50 text-amber-700",
    suspended: "bg-amber-50 text-amber-700",
    expired: "bg-rose-50 text-rose-700",
    terminated: "bg-rose-50 text-rose-700",
    resigned: "bg-gray-100 text-gray-600",
    replaced: "bg-gray-100 text-gray-600",
    // Iter60.p27 — distinctive amber pill so weekend-worked days stand
    // out from plain weekend (grey) and regular weekday-present (green).
    weekend_present: "bg-amber-50 text-amber-800 border border-amber-300",
};

export function statusBadgeClass(status) {
    return STATUS_BADGE[status] || "bg-gray-100 text-gray-600";
}

export const EMPLOYMENT_BADGE = {
    casual: "bg-amber-50 text-amber-700 border border-amber-200",
    non_casual: "bg-[color:var(--asl-navy-50)] text-[color:var(--asl-navy)] border border-[color:var(--asl-navy-50)]",
};
